import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram } from "lucide-react";
import { CONTACT, telHref, telHref2, mailtoHref } from "@/config/contact";
import { useI18n } from "@/i18n/i18n";

export default function Footer() {
  const { tr } = useI18n();
  const links = [
    { to: "/", label: tr.nav.home },
    { to: "/servicios", label: tr.nav.services },
    { to: "/nosotros", label: tr.nav.about },
    { to: "/testimonios", label: tr.nav.testimonials },
    { to: "/contacto", label: tr.nav.contact },
  ];

  return (
    <footer
      className="relative mt-24 border-t border-white/10"
      style={{ background: "linear-gradient(180deg, #07102E 0%, #050B24 100%)" }}
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-4">
            <img
              src={CONTACT.logoIconUrl}
              alt=""
              loading="eager"
              decoding="async"
              className="h-20 w-20 object-contain pointer-events-none"
              style={{
                mixBlendMode: "screen",
                filter: "brightness(1.2) contrast(2.2) saturate(1.25)",
                WebkitMaskImage:
                  "radial-gradient(circle at 50% 50%, #000 45%, rgba(0,0,0,0.7) 60%, transparent 78%)",
                maskImage:
                  "radial-gradient(circle at 50% 50%, #000 45%, rgba(0,0,0,0.7) 60%, transparent 78%)",
              }}
            />
            <span className="font-heading text-2xl font-semibold text-white tracking-tight whitespace-nowrap">
              Master Credit <span className="text-[color:var(--mc-cyan)]">Group</span>
            </span>
          </div>
          <p className="mt-6 text-white/75 max-w-md leading-relaxed">{tr.footer.tagline}</p>
          <div className="mt-6 flex items-center gap-3">
            <a href={CONTACT.social.facebook} target="_blank" rel="noopener noreferrer" className="h-10 w-10 rounded-full bg-white/10 hover:bg-[color:var(--mc-cyan)]/30 hover:text-[color:var(--mc-cyan)] flex items-center justify-center transition-colors" aria-label="Facebook" data-testid="social-facebook">
              <Facebook className="h-4 w-4" />
            </a>
            <a href={CONTACT.social.instagram} target="_blank" rel="noopener noreferrer" className="h-10 w-10 rounded-full bg-white/10 hover:bg-[color:var(--mc-cyan)]/30 hover:text-[color:var(--mc-cyan)] flex items-center justify-center transition-colors" aria-label="Instagram" data-testid="social-instagram">
              <Instagram className="h-4 w-4" />
            </a>
            <a href={CONTACT.social.tiktok} target="_blank" rel="noopener noreferrer" className="h-10 w-10 rounded-full bg-white/10 hover:bg-[color:var(--mc-cyan)]/30 hover:text-[color:var(--mc-cyan)] flex items-center justify-center transition-colors" aria-label="TikTok" data-testid="social-tiktok">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.83 20.1a6.34 6.34 0 0 0 10.86-4.43V8.83a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.87-.26Z"/>
              </svg>
            </a>
            <a href={`mailto:${CONTACT.email}`} className="h-10 w-10 rounded-full bg-white/10 hover:bg-[color:var(--mc-cyan)]/30 hover:text-[color:var(--mc-cyan)] flex items-center justify-center transition-colors" aria-label="Email" data-testid="social-email">
              <Mail className="h-4 w-4" />
            </a>
          </div>
          <div className="mt-5 flex flex-col gap-1.5 text-sm text-white/70">
            <a href={CONTACT.social.instagram} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-[color:var(--mc-cyan)] transition-colors" data-testid="social-handle-ig">
              <Instagram className="h-3.5 w-3.5 text-[color:var(--mc-gold)]" /> @mastercreditgroup
            </a>
            <a href={CONTACT.social.facebook} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-[color:var(--mc-cyan)] transition-colors" data-testid="social-handle-fb">
              <Facebook className="h-3.5 w-3.5 text-[color:var(--mc-gold)]" /> Master Credit Group
            </a>
            <a href={CONTACT.social.tiktok} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-[color:var(--mc-cyan)] transition-colors" data-testid="social-handle-tk">
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-[color:var(--mc-gold)]" fill="currentColor" aria-hidden="true">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.83 20.1a6.34 6.34 0 0 0 10.86-4.43V8.83a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.87-.26Z"/>
              </svg>
              @mastercreditgroup
            </a>
          </div>
        </div>

        <div>
          <p className="overline text-white/60 mb-5">{tr.footer.nav_overline}</p>
          <ul className="space-y-3 text-white/85">
            {links.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="hover:text-white" data-testid={`footer-link-${l.to.replace("/", "") || "home"}`}>
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="overline text-white/60 mb-5">{tr.footer.contact_overline}</p>
          <ul className="space-y-4 text-white/85">
            <li className="flex items-start gap-3">
              <Phone className="h-4 w-4 mt-1 text-[color:var(--mc-gold)]" />
              <div className="flex flex-col gap-1">
                <a href={telHref} className="hover:text-white" data-testid="footer-phone">
                  {CONTACT.phoneDisplay}
                </a>
                <a href={telHref2} className="hover:text-white" data-testid="footer-phone-2">
                  {CONTACT.phoneDisplay2}
                </a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="h-4 w-4 mt-1 text-[color:var(--mc-gold)]" />
              <a href={mailtoHref} className="hover:text-white break-all" data-testid="footer-email">
                {CONTACT.email}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="h-4 w-4 mt-1 text-[color:var(--mc-gold)]" />
              <a
                href={CONTACT.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white leading-snug"
                data-testid="footer-address"
              >
                {CONTACT.addressLine1}<br />
                {CONTACT.addressLine2}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-white/60">
          <p>© {new Date().getFullYear()} {CONTACT.brand}. {tr.footer.copyright}</p>
          <p className="italic">{tr.footer.slogan}</p>
        </div>
      </div>
    </footer>
  );
}
